import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Container,
  Alert,
  AlertTitle,
  Backdrop
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import API from '../API';
import UserProfile from '../Pages/Dashboard/Users/userprofile';
import MediaKit from '../Pages/MediaKit/mediaKit';
import PageTitle from '../Components/PageTitle';
import useAlertDialog from '../Components/useAlertDialog';
import AlertDialog from '../Components/AlertDialog';

/**
 * GlobalUserProfile - A unified component that displays user profiles or creator media kits
 * Based on the slug provided in the URL (/@slug)
 * 
 * SIMPLIFIED: ID resolution now handled entirely on the backend
 */
export default function GlobalUserProfile() {
  const { slug } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [profileData, setProfileData] = useState(null);
  const [profileType, setProfileType] = useState(null); // 'user' or 'creator'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setError('No profile identifier provided');
      setLoading(false);
      return;
    }

    const fetchProfileData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('[GlobalUserProfile] Fetching profile for slug:', slug);
        
        // Check if slug is numeric (user ID) or string (creator username)
        const isNumeric = /^\d+$/.test(slug);
        
        if (isNumeric) {
          // Numeric slug - fetch as user (backend handles ID resolution)
          console.log('[GlobalUserProfile] Fetching as user ID:', slug);
          try {
            const userData = await API.users.fetchPublicDetails(slug);
            if (userData) {
              console.log('[GlobalUserProfile] User data fetched successfully');
              setProfileData(userData);
              setProfileType('user');
              setLoading(false);
              return;
            }
          } catch (userError) {
            console.error('User fetch failed:', userError);
            throw new Error(`User not found for ID ${slug}`);
          }
        } else {
          // String slug - fetch as creator username
          console.log('[GlobalUserProfile] Fetching as creator username:', slug);
          try {
            const creatorData = await API.creators.fetchDetails(slug);
            if (creatorData && creatorData.creator) {
              console.log('[GlobalUserProfile] Creator found');
              setProfileData(creatorData);
              setProfileType('creator');
              setLoading(false);
              return;
            }
          } catch (creatorError) {
            console.error('Creator fetch failed:', creatorError);
            throw new Error(`Creator not found for username @${slug}`);
          }
        }

        throw new Error(`Profile not found for @${slug}`);
      } catch (err) {
        console.error('Failed to fetch profile data:', err);
        setError(err.message || 'Failed to load profile');
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [slug]);

  // Handle errors with user-friendly dialog
  useEffect(() => {
    if (error) {
      openDialog(
        'Profile Not Found',
        <>
          We couldn't find a profile for @{slug}.<br />
          Please check the username and try again.
        </>,
        () => {
          closeDialog();
          navigate('/');
        },
        closeDialog,
        'Go Home',
        'Try Again'
      );
    }
  }, [error, slug, openDialog, closeDialog, navigate]);

  // Loading state
  if (loading) {
    return (
      <>
        <PageTitle title={`Profile Loading...`} />
        <Backdrop
          open
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CircularProgress color="inherit" size={60} />
            <Typography variant="h6" color="inherit">
              Loading Profile...
            </Typography>
          </Box>
        </Backdrop>
      </>
    );
  }

  // Error state
  if (error && !profileData) {
    return (
      <>
        <PageTitle title={`@${slug} - Profile Not Found`} />
        <AlertDialog alertState={dialogState} />
        <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            <AlertTitle>Profile Not Found</AlertTitle>
            We couldn't find a profile for @{slug}. Please check the username and try again.
          </Alert>
        </Container>
      </>
    );
  }

  // Success state - render the appropriate profile component
  if (profileType === 'creator' && profileData) {
    return (
      <>
        <PageTitle title={`@${slug} - Creator Profile`} />
        <MediaKit
          initialCreatorData={profileData}
          creatorIdFromParams={slug}
          hideHeader={true}
        />
      </>
    );
  }

  if (profileType === 'user' && profileData) {
    return (
      <>
        <UserProfile
          initialUserData={profileData}
          userIdFromParams={slug}
        />
      </>
    );
  }

  // Fallback
  return (
    <>
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">
          Unable to determine profile type
        </Typography>
      </Container>
    </>
  );
}

/**
 * Export additional utility functions for profile type detection
 */
export const ProfileUtils = {
  /**
   * Determines if a slug is a numeric user ID or string creator username
   */
  predictProfileType: (slug) => {
    if (/^\d+$/.test(slug)) {
      return 'user';
    }
    return 'creator';
  },
  
  /**
   * Validates if a slug could be a valid profile identifier
   */
  isValidSlug: (slug) => {
    if (!slug || typeof slug !== 'string') return false;
    return /^[a-zA-Z0-9_.-]{1,50}$/.test(slug);
  }
};